import CtaLink, { TextCtaLink } from '@/components/CtaLink';
import CtaText from '@/components/CtaText';
import RightCheveron from '@/components/Icons/RightCheveron';
import ImageMarqueeCarousel from '@/components/ImageMarqueeCarousel';
import PageLayoutV2 from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import {
  CLIENT_ARTIST_APPLICATIONS_URL,
  CLIENT_DISCOVER_URL,
  CLIENT_SIGNUP_VIA_EMAIL_URL,
  CLIENT_USER_COLLECTION_URL,
  defaultImageMarqueeCarouselImages,
} from '@/utils/constants';
import Image from "next/legacy/image";
import Link from 'next/link';
import olea_logo from 'public/images/olea_logo.svg';
import { ReactNode } from 'react';

export default function AboutPage() {
  return (
    <PageLayoutV2>
      <NavBarLightTheme />
      <div className="relative w-full mt-2.4 lgt:mt-5.6 overflow-hidden">
        <TwelveColGrid
          gap="gap-x-0.8 lgt:gap-x-2.4"
          my="mb-4.8 lgt:mb-40"
          addClassStyles="overflow-visible copy-xl"
        >
          <h6 className="col-span-12 text-black mb-1.6">ABOUT US</h6>
          <div className="flex flex-col col-span-12 mb-3.2 lgt:mb-6.4 ">
            <h6 className="text-grey-300 mb-1.6">Love the art</h6>
            <h6 className="text-grey-300 mb-1.6">Discover the artist</h6>
            <h6 className="text-grey-300 mb-1.6">Join the community</h6>
            <h6 className="text-grey-300 mb-1.6">Collect smarter</h6>
            <h6 className="text-grey-300 mb-1.6">Connect better</h6>
          </div>
        </TwelveColGrid>

        <ImageMarqueeCarousel imgUrlArray={defaultImageMarqueeCarouselImages} />
        <TwelveColGrid my="mt-5.6 lgt:mt-36">
          <h6 className="col-span-12 text-black copy-xl">THIS IS OLEA</h6>
        </TwelveColGrid>
      </div>
      <TwelveColGrid gap="gap-0.8 lgt:gap-2.4" my="my-64 lgt:my-56">
        <div className="col-span-12 lgt:col-span-4 mb-3.2 lgt:mb-4.8">
          <h3 className="copy-xl">UNITING CREATORS &amp; COLLECTORS</h3>
        </div>
        <div className="col-span-12 mb-6.4 lgt:col-span-6 lgt:col-start-7 text-body lgt:mb-0">
          We connect artists with collectors worldwide, helping them forge
          strong relationships that facilitate the creation and distribution of
          art.
        </div>
        <div className="col-span-12 mb-0.8 lgt:mb-0">
          <AboutCard
            bgImage={
              <CardBgImage
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1648401778/AboutPage/collection_management_card_no_blur_vcgldn.png'
                }
                size={'h-[25rem] w-[70rem]'}
                position={
                  '-right-[50rem] -top-[9.5rem] lgt:-top-14 lgt:-right-32'
                }
              />
            }
            title={'COLLECTION MANAGMENT'}
            copy={
              'Build, manage and evolve your art collection all from one place.'
            }
            href={CLIENT_USER_COLLECTION_URL}
          />
        </div>

        <div className="col-span-12 lgt:col-span-6 mb-0.8 lgt:mb-0">
          <AboutCard
            bgImage={
              <CardBgImage
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1647984647/AboutPage/marketplace_card_hhjang.png'
                }
                size={'h-[22.9rem] w-[22.9rem]'}
                position={'top-0 -right-[5rem] lgt:right-0'}
              />
            }
            title={'DISCOVER'}
            copy={
              'Find, explore and unlock access to the rare artworks and exclusive releases you love. New artworks added daily.'
            }
            href={CLIENT_DISCOVER_URL}
          />
        </div>
        <div className="col-span-12 lgt:col-span-6 mb-1.6 lgt:mb-2.4">
          <AboutCard
            bgImage={
              <CardBgImage
                src={
                  'https://res.cloudinary.com/hmkqf8kua/image/upload/v1647984608/AboutPage/community_card_vwuqv2.png'
                }
                size={'h-80 w-32.0 lgt:h-[32.4rem] lgt:w-[47rem]'}
                position={'top-0 -right-44 lgt:-right-24 lgt:-top-20'}
              />
            }
            title={'COMMUNITY'}
            copy={
              'Connect directly with artists and other collectors across a trusted, verified community.'
            }
            href={undefined}
            icon={
              <span className="flex flex-col items-end">
                <RightCheveron />
                <span className="text-caption">COMING SOON</span>
              </span>
            }
          />
        </div>
        <div className="flex flex-col items-center col-span-12 mt-2.4">
          <CtaLink href={CLIENT_SIGNUP_VIA_EMAIL_URL} width="w-full max-w-34.4">
            <CtaText>SIGN UP</CtaText>
          </CtaLink>
          <div className="col-span-12 mt-12 text-center">
            <div className="text-s text-black-300 mb-0.8 flex">
              Are you an artist looking to join OLEA?
              <TextCtaLink
                margin="ml-0.3"
                color="text-black"
                href={CLIENT_ARTIST_APPLICATIONS_URL}
              >
                Contact us!
              </TextCtaLink>
            </div>
          </div>
        </div>
      </TwelveColGrid>
    </PageLayoutV2>
  );
}
function CardBgImage({
  src,
  size,
  position,
}: {
  src: string;
  size: string;
  position: string;
}) {
  return (
    <div className={`absolute ${position}`}>
      <div className={`relative ${size}`}>
        <Image
          src={src}
          layout="fill"
          alt="bg decoraction image of art"
          objectFit="cover"
        />
      </div>
    </div>
  );
}

function AboutCard({
  bgImage,
  title,
  copy,
  href,
  icon = <RightCheveron />,
}: {
  bgImage: JSX.Element;
  title: string;
  copy: string;
  href: string | undefined;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start justify-between h-[40rem] w-full bg-grey-100 relative pt-2.4 pr-1.6 pb-1.6 pl-2.4 lgt:pr-2.4 lgt:pb-2.4 overflow-hidden">
      {bgImage}
      <Image height={22} alt="OLEA Logo" layout="intrinsic" src={olea_logo} />
      <div className="relative flex flex-col w-full">
        <h5 className="copy-l mb-0.8">{title}</h5>
        <div className="flex flex-col w-full lgt:flex-row lgt:justify-between">
          <span className="text-black text-opacity-50 text-body max-w-[40rem]">
            {copy}
          </span>
          <div className="h-6.4 flex items-center justify-center flex-shrink-0 self-end">
            {icon}
          </div>
        </div>
      </div>
      {href && (
        (<Link href={href} className="absolute top-0 left-0 w-full h-full">

        </Link>)
      )}
    </div>
  );
}
